import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core";
import ReactEcharts from "echarts-for-react";

function bumpFontSize(value, factor) {
  if (value == null) return value;
  if (typeof value === "number") return Math.round(value * factor);
  if (typeof value === "string") {
    const m = value.match(/^(\d+(?:\.\d+)?)(px)?$/);
    if (!m) return value;
    const n = Number(m[1]);
    if (Number.isNaN(n)) return value;
    const bumped = Math.round(n * factor);
    return m[2] ? `${bumped}px` : String(bumped);
  }
  return value;
}

function deepClonePreserveFns(obj) {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(deepClonePreserveFns);
  const out = {};
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    out[k] = typeof v === "function" ? v : deepClonePreserveFns(v);
  }
  return out;
}

function bumpOptionTypography(option, { factor = 1.25 } = {}) {
  if (!option || typeof option !== "object") return option;
  const next = deepClonePreserveFns(option);

  const bumpInPlace = (node) => {
    if (!node || typeof node !== "object") return;

    // Known ECharts typography knobs.
    const directFontKeys = ["fontSize"];
    directFontKeys.forEach((k) => {
      if (k in node) node[k] = bumpFontSize(node[k], factor);
    });

    const nestedTextStyleKeys = [
      "textStyle",
      "nameTextStyle",
      "axisLabel",
      "axisPointer",
      "label",
      "labelLine",
      "pageTextStyle",
      "title",
      "subtextStyle",
    ];
    nestedTextStyleKeys.forEach((k) => {
      if (node[k] && typeof node[k] === "object") bumpInPlace(node[k]);
    });

    // Walk children.
    Object.keys(node).forEach((k) => {
      const v = node[k];
      if (!v) return;
      if (typeof v === "object") {
        if (Array.isArray(v)) v.forEach((x) => bumpInPlace(x));
        else bumpInPlace(v);
      }
    });
  };

  bumpInPlace(next);
  return next;
}

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: "100%",
    background: "#fff",
  },
  rootFullscreen: {
    width: "100%",
    height: "100%",
    background: "#fff",
  },
  chart: {
    width: "100%",
  },
  btn: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 3,
    width: "auto",
    height: "auto",
    padding: 0,
    lineHeight: 0,
    borderRadius: 0,
    border: "none",
    background: "transparent",
    color: "#64748B",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "none",
    transition: "transform 0.12s, background 0.12s, box-shadow 0.12s",
    "&:hover": {
      transform: "translateY(-1px)",
      background: "transparent",
      boxShadow: "none",
    },
    "&:active": { transform: "translateY(0px)" },
    "&:focus-visible": {
      outline: "2px solid rgba(100,116,139,0.45)",
      outlineOffset: 2,
    },
  },
  hint: {
    position: "absolute",
    top: 12,
    right: 50,
    zIndex: 3,
    fontSize: 11,
    fontWeight: 700,
    color: "rgba(15,23,42,0.72)",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(2,6,23,0.12)",
    borderRadius: 999,
    padding: "6px 10px",
    boxShadow: "0 10px 24px rgba(2,6,23,0.10)",
    pointerEvents: "none",
  },
}));

function ExpandIcon({ isExit }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {isExit ? (
        <>
          {/* Exit fullscreen: arrows pointing inward */}
          <polyline points="9 3 9 7 5 7" />
          <polyline points="15 21 15 17 19 17" />
          <polyline points="21 9 17 9 17 5" />
          <polyline points="3 15 7 15 7 19" />
          <line x1="9" y1="7" x2="5" y2="3" />
          <line x1="15" y1="17" x2="19" y2="21" />
          <line x1="17" y1="9" x2="21" y2="5" />
          <line x1="7" y1="15" x2="3" y2="19" />
        </>
      ) : (
        <>
          {/* Enter fullscreen: corner brackets */}
          <polyline points="9 3 3 3 3 9" />
          <polyline points="15 3 21 3 21 9" />
          <polyline points="3 15 3 21 9 21" />
          <polyline points="21 15 21 21 15 21" />
        </>
      )}
    </svg>
  );
}

const FullscreenEcharts = forwardRef(function FullscreenEcharts(
  { option, height = 200, notMerge = true, style, showHint = false, showButton = true, ...rest },
  ref,
) {
  const classes = useStyles();
  const hostRef = useRef(null);
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      const el = hostRef.current;
      setIsFullscreen(!!el && document.fullscreenElement === el);
    };
    document.addEventListener("fullscreenchange", onChange);
    onChange();
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    // ECharts needs an explicit resize after fullscreen toggles.
    const api = chartRef.current;
    if (!api) return;
    const instance = api.getEchartsInstance?.();
    if (!instance) return;
    const t = setTimeout(() => instance.resize?.(), 60);
    return () => clearTimeout(t);
  }, [isFullscreen]);

  const fillParent = height === "100%";

  const chartStyle = useMemo(() => {
    const base = { height: isFullscreen || fillParent ? "100%" : height, width: "100%" };
    return { ...base, ...(style || {}) };
  }, [height, isFullscreen, style, fillParent]);

  const effectiveOption = useMemo(() => {
    if (!isFullscreen) return option;
    return bumpOptionTypography(option, { factor: 1.25 });
  }, [isFullscreen, option]);

  const toggle = async () => {
    const el = hostRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
      } else {
        await el.requestFullscreen?.();
      }
    } catch {
      // ignore — browser may block without user gesture
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      toggleFullscreen: toggle,
      enterFullscreen: async () => {
        const el = hostRef.current;
        if (!el) return;
        try {
          if (!document.fullscreenElement) await el.requestFullscreen?.();
        } catch {
          // ignore
        }
      },
      exitFullscreen: async () => {
        try {
          if (document.fullscreenElement) await document.exitFullscreen?.();
        } catch {
          // ignore
        }
      },
      get isFullscreen() {
        return isFullscreen;
      },
    }),
    [isFullscreen],
  );

  return (
    <div
      ref={hostRef}
      className={`${classes.root} ${isFullscreen ? classes.rootFullscreen : ""}`}
      style={fillParent && !isFullscreen ? { height: "100%", display: "flex", flexDirection: "column" } : undefined}
    >
      {showHint && !isFullscreen && <div className={classes.hint}>Fullscreen</div>}
      {showButton && (
        <button
          type="button"
          className={classes.btn}
          onClick={toggle}
          aria-label={isFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
          title={isFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
        >
          <ExpandIcon isExit={isFullscreen} />
        </button>
      )}
      <ReactEcharts
        ref={chartRef}
        option={effectiveOption}
        notMerge={notMerge}
        style={chartStyle}
        className={classes.chart}
        {...rest}
      />
    </div>
  );
});

export default FullscreenEcharts;

